/**
 * Canonical brand data for TRBD.
 *
 * Single source of truth for:
 *   - Founder profile
 *   - Social channels (personal + company Telegram bot)
 *   - WhatsApp numbers
 *   - App-store developer links
 *   - Headline stats
 *   - Flagship products
 *
 * Every entry carries everything the UI needs to render itself (urls, labels,
 * brand colors), so call-sites stop hardcoding hex values and copy-pasting URLs.
 */

export const FOUNDER = {
    nameAr: 'المهندس أحمد علولو',
    nameEn: 'Eng. Ahmed Allulu',
    titleEn: 'CEO & Founder',
    titleAr: 'المؤسس والرئيس التنفيذي',
    avatar: '/assets/robot-avatar.png',
    bioAr: 'مؤسس TRBD و CarWhats Group. 7+ سنوات خبرة في بناء منصات ومنتجات رقمية تخدم عملاء في أكثر من دولة حول العالم.',
    bioEn: 'Founder of TRBD & CarWhats Group. 7+ years building digital platforms and products serving clients worldwide.',
} as const;

/**
 * Full founder roster for the marketing site. Ahmed and Tarek are co-founders
 * and both serve as CEO. Each carries a signed pull-quote for the founder
 * timeline section; photos live under /public/assets.
 */
export const FOUNDERS = [
    {
        key: 'ahmed',
        nameAr: 'المهندس أحمد علولو',
        nameEn: 'Eng. Ahmed Allulu',
        titleAr: 'المؤسس والرئيس التنفيذي',
        titleEn: 'CEO & Founder',
        monogram: 'أ',
        photo: '/assets/End-Ahmed.jpeg',
        backgroundAr: 'هندسة مدنية',
        backgroundEn: 'Civil Engineering',
        quoteAr:
            'درستُ الهندسة المدنية فتعلمتُ أن الأساس أهم من الواجهة، وأن البناء الذي يدوم يبدأ من تحت الأرض حيث لا يرى الناس. حملتُ هذا الدرس معي إلى TRBD؛ فالشركات، كالأبنية، لا تُقاس بارتفاعها بل بعمق أساساتها. نحن لا نبني منتجات رقمية فحسب، نحن نهندس علاقات، ونصبّ الثقة طبقةً فوق طبقة. سبع سنوات مضت، وما زلنا نبني… ولا نستعجل.',
        quoteEn:
            "I studied civil engineering and learned that the foundation matters more than the façade — that lasting buildings start below ground, where no one sees. I carried that lesson to TRBD: companies, like buildings, aren't measured by their height but by the depth of their foundations. We don't just build digital products — we engineer relationships and pour trust layer over layer. Seven years in, and we're still building… unhurried.",
    },
    {
        key: 'tarek',
        nameAr: 'المهندس طارق علولو',
        nameEn: 'Eng. Tarek Allulu',
        titleAr: 'الشريك المؤسس والرئيس التنفيذي',
        titleEn: 'CEO & Co-Founder',
        monogram: 'ط',
        photo: '/assets/Eng-Tarek.jpeg',
        backgroundAr: 'هندسة برمجيات',
        backgroundEn: 'Software Engineering',
        quoteAr:
            'تعلّمتُ من البرمجة أن أجمل الأكواد هي التي لا تراها، لكنها تعمل بصمت وتُتقن عملها. هكذا أحاول أن أكون؛ خلف كل منتج نبنيه في TRBD، هناك ساعات طويلة لا يراها أحد، وقرارات صغيرة تصنع الفرق. لا أؤمن بالحلول السريعة، أؤمن بالحلول التي تصمد. المهندس الجيد يكتب برنامجاً يعمل، والمهندس الذي نطمح أن نكونه يكتب برنامجاً يستحق أن يُورَّث.',
        quoteEn:
            "Programming taught me that the most beautiful code is the code you never see — the kind that runs quietly and does its job perfectly. That's what I try to be. Behind every product we build at TRBD, there are long hours no one sees and small decisions that make all the difference. I don't believe in quick fixes; I believe in solutions that last. A good engineer writes code that works. The engineer we aspire to be writes code worth inheriting.",
    },
    {
        key: 'ahmad-alkhalil',
        nameAr: 'أ. أحمد الخليل',
        nameEn: 'Ahmad Al-Khalil',
        titleAr: 'الرئيس التنفيذي',
        titleEn: 'CEO',
        monogram: 'أ',
        photo: '/assets/Ahmad Al-khalil.jpeg',
        backgroundAr: 'إدارة أعمال',
        backgroundEn: 'Business Management',
        quoteAr:
            'القيادة الحقيقية تبدأ من الإنصات لا من الكلام. تعلّمتُ أن أفضل القرارات تُصنع حين تضع نفسك مكان فريقك، وأن النجاح ليس وجهة بل طريقة تفكير يومية. أسعى دائماً لبناء فِرَق تؤمن بما تفعله قبل أن تُطالَب به، لأن العمل الذي يُبنى على الثقة والشغف لا يحتاج رقابة — يحتاج فقط رؤية واضحة وأناساً يستحقون أن تثق بهم.',
        quoteEn:
            "True leadership begins with listening, not speaking. I've learned that the best decisions are made when you put yourself in your team's shoes, and that success isn't a destination — it's a daily mindset. I always strive to build teams that believe in what they do before they're ever asked to, because work built on trust and passion doesn't need oversight — it just needs a clear vision and people worthy of that trust.",
    },
] as const;

export type FounderEntry = (typeof FOUNDERS)[number];

/**
 * Key team leads — senior roles that sit under the founders on the org chart
 * (rendered below the founders section on the home page).
 */
export const TEAM_LEADS = [
    {
        key: 'asem',
        nameAr: 'المهندس عاصم علولو',
        nameEn: 'Eng. Asem Allulu',
        titleAr: 'مطوّر Full-Stack',
        titleEn: 'Full-Stack Developer & Architecture Expert',
        monogram: 'ع',
        photo: '/assets/Asem Allulu.jpeg',
        backgroundAr: 'هندسة برمجيات',
        backgroundEn: 'Software Engineering',
    },
    {
        key: 'dima',
        nameAr: 'المهندسة ديما الخطيب',
        nameEn: 'Eng. Dima Al-Khatib',
        titleAr: 'مديرة المشاريع',
        titleEn: 'Project Manager',
        monogram: 'د',
        photo: '/assets/Eng-Dima Al-khatib.jpeg',
        backgroundAr: 'هندسة البرمجيات',
        backgroundEn: 'Software Engineering',
    },
] as const;

export type TeamLead = (typeof TEAM_LEADS)[number];

export const SOCIALS = {
    linkedin: {
        url: 'https://www.linkedin.com/in/ahmed-allulu-96605b404/',
        labelAr: 'لينكد إن',
        labelEn: 'LinkedIn',
        brand: '#0a66c2',
    },
    youtube: {
        url: 'https://www.youtube.com/channel/UC2h0v_PBUQzODLNT9EIUGzw',
        labelAr: 'يوتيوب',
        labelEn: 'YouTube',
        brand: '#FF0000',
    },
    facebook: {
        url: 'https://www.facebook.com/trbd.tech.sy',
        labelAr: 'فيسبوك',
        labelEn: 'Facebook',
        brand: '#1877F2',
    },
    telegram: {
        url: 'https://t.me/trbd_sybot',
        labelAr: 'تيليجرام',
        labelEn: 'Telegram',
        brand: '#229ED9',
    },
} as const;

export type SocialKey = keyof typeof SOCIALS;

export const WHATSAPP = {
    tr: {
        url: 'https://wa.me/905537323153',
        display: '+90 553 732 3153',
        labelAr: 'واتساب تركيا',
        labelEn: 'WhatsApp Turkey',
    },
    sy: {
        url: 'https://wa.me/963992367582',
        display: '+963 992 367 582',
        labelAr: 'واتساب سوريا',
        labelEn: 'WhatsApp Syria',
    },
} as const;

export const APP_STORES = {
    googlePlay: {
        url: 'https://play.google.com/store/apps/dev?id=5700825962896136120',
        labelEn: 'Google Play',
        captionEn: 'GET IT ON',
    },
    appStore: {
        url: 'https://apps.apple.com/us/developer/ahmed-allulu/id1762488678',
        labelEn: 'App Store',
        captionEn: 'Download on the',
    },
} as const;

export const STATS = {
    projects: '39+',
    clients: '45+',
    yearsExperience: '7+',
    satisfaction: '99%',
} as const;

/**
 * Flagship products. Real positioning + ecosystem clarity.
 */
export const FLAGSHIPS = [
    {
        key: 'kasioon',
        name: 'Kasioon',
        nameAr: 'قاسيون',
        url: 'https://kasioon.com',
        descAr: 'منصة إعلانات مبوبة (Marketplace)',
        descEn: 'Classified marketplace platform',
        descArLong: 'منصة إعلانات مبوبة متكاملة تربط بين البائعين والمشترين في عدة قطاعات مثل العقارات، السيارات، والخدمات. تمثل حجر الأساس في منظومة المنتجات من حيث خلق السيولة داخل السوق الرقمي.',
        descEnLong: 'A full-scale classifieds marketplace connecting buyers and sellers across real estate, automotive, and services. Serves as the core liquidity engine of the ecosystem.',
        gradient: 'from-purple-500/20 to-pink-500/10',
        gradientRich: 'from-purple-500/20 via-fuchsia-500/10 to-pink-500/5',
        accent: 'bg-purple-500',
        tags: ['Marketplace', 'React', 'Node.js', 'iOS', 'Android'],
    },
    {
        key: 'carwhats-group',
        name: 'CarWhats Group',
        nameAr: 'مجموعة كارواتس',
        url: 'https://carwhats-group.com',
        descAr: 'بوابة شركات (Corporate Portal)',
        descEn: 'Corporate group portal',
        descArLong: 'البوابة الرسمية لمجموعة CarWhats، تعرض الكيانات والخدمات المختلفة تحت مظلة واحدة مع تركيز على الهوية المؤسسية والربط بين المنتجات.',
        descEnLong: 'The official corporate portal of CarWhats Group, connecting multiple services and entities under a unified brand identity.',
        gradient: 'from-blue-500/20 to-cyan-500/10',
        gradientRich: 'from-blue-500/20 via-cyan-500/10 to-sky-500/5',
        accent: 'bg-blue-500',
        tags: ['Corporate', 'Next.js', 'TypeScript'],
    },
    {
        key: 'carwhats',
        name: 'CarWhats',
        nameAr: 'كارواتس',
        url: 'https://carwhats.com',
        descAr: 'سوق سيارات متخصص',
        descEn: 'Automotive marketplace',
        descArLong: 'منصة متخصصة في قطاع السيارات تربط المستخدمين بالمعارض والخدمات المرتبطة، مع تجربة موجهة أكثر مقارنة بالمنصات العامة.',
        descEnLong: 'A vertical automotive marketplace connecting users with dealers and related services through a focused experience.',
        gradient: 'from-emerald-500/20 to-teal-500/10',
        gradientRich: 'from-emerald-500/20 via-teal-500/10 to-green-500/5',
        accent: 'bg-emerald-500',
        tags: ['Marketplace', 'Automotive', 'Flutter', 'Firebase'],
    },
    {
        key: 'megaquantum-nl',
        name: 'Mega Quantum',
        nameAr: 'ميجا كوانتم',
        url: 'https://megaquantum.nl',
        descAr: 'شركة حلول تقنية (B2B)',
        descEn: 'B2B tech solutions platform',
        descArLong: 'منصة تمثل شركة تقنية تقدم حلول برمجية وخدمات رقمية للأسواق الأوروبية، مع تركيز على تقديم الخدمات للشركات (B2B).',
        descEnLong: 'A European-facing tech platform delivering software solutions and digital services to businesses (B2B).',
        gradient: 'from-indigo-500/20 to-violet-500/10',
        gradientRich: 'from-indigo-500/20 via-violet-500/10 to-purple-500/5',
        accent: 'bg-indigo-500',
        tags: ['B2B', 'Next.js', 'TypeScript'],
    },
    {
        key: 'megaquantum-net',
        name: 'Mega Quantum Labs',
        nameAr: 'مختبرات ميجا كوانتم',
        url: 'https://megaquantum.net',
        descAr: 'مركز المنتجات والأبحاث',
        descEn: 'Products & R&D hub',
        descArLong: 'واجهة مخصصة لعرض المنتجات والتجارب التقنية ضمن منظومة Mega Quantum، تركز على الابتكار واستكشاف أفكار جديدة.',
        descEnLong: 'A dedicated hub for showcasing products and experimental projects within the Mega Quantum ecosystem.',
        gradient: 'from-violet-500/20 to-fuchsia-500/10',
        gradientRich: 'from-violet-500/20 via-fuchsia-500/10 to-pink-500/5',
        accent: 'bg-violet-500',
        tags: ['R&D', 'Next.js', 'MDX'],
    },
    {
        key: 'vivaverse',
        name: 'Vivaverse',
        nameAr: 'فيفا فيرس',
        url: 'https://vivaverse.top',
        descAr: 'منصة اجتماعية للمبدعين',
        descEn: 'Creator social platform',
        descArLong: 'منصة اجتماعية تربط المبدعين بجمهورهم وتتيح التفاعل والمحتوى، مع تركيز على تجربة استخدام سريعة وخفيفة.',
        descEnLong: 'A social platform connecting creators with their audience through fast and lightweight interactions.',
        gradient: 'from-pink-500/20 to-rose-500/10',
        gradientRich: 'from-pink-500/20 via-rose-500/10 to-red-500/5',
        accent: 'bg-pink-500',
        tags: ['Social', 'WebSocket', 'MongoDB'],
    },
    {
        key: 'sport24seven',
        name: 'Sport 24/7',
        nameAr: 'سبورت 24/7',
        url: 'https://sport24seven.com',
        descAr: 'منصة إعلام رياضي',
        descEn: 'Sports media platform',
        descArLong: 'منصة إعلامية تقدم أخبار ونتائج رياضية بشكل مستمر، مع التركيز على السرعة والبساطة في عرض المحتوى.',
        descEnLong: 'A sports media platform delivering news, live scores, and updates with a fast and focused experience.',
        gradient: 'from-amber-500/20 to-orange-500/10',
        gradientRich: 'from-amber-500/20 via-orange-500/10 to-yellow-500/5',
        accent: 'bg-amber-500',
        tags: ['Media', 'Realtime', 'Next.js'],
    },
    {
        key: 'oscar-news',
        name: 'Oscar News',
        nameAr: 'أوسكار نيوز',
        url: 'https://oscar-news.com',
        descAr: 'منصة أخبار رقمية',
        descEn: 'Digital news platform',
        descArLong: 'منصة إخبارية رقمية تركز على تقديم محتوى يومي سريع مع تجربة قراءة بسيطة ومنظمة.',
        descEnLong: 'A digital news platform focused on delivering fast daily content with a clean reading experience.',
        gradient: 'from-sky-500/20 to-cyan-500/10',
        gradientRich: 'from-sky-500/20 via-cyan-500/10 to-blue-500/5',
        accent: 'bg-sky-500',
        tags: ['Media', 'CMS', 'SEO'],
    },
] as const;

export type Flagship = (typeof FLAGSHIPS)[number];
